import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { IMC_AUTH_CONFIG } from '../auth.config';
import { filter, map, take } from 'rxjs/operators';
import { firstValueFrom, race, timer } from 'rxjs';

export const authGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const config = inject(IMC_AUTH_CONFIG);

  // Check if already authenticated
  if (authService.isAuthenticated()) {
    return true;
  }

  // Wait for MSAL initialization and redirect processing to complete (with 10 second timeout)
  try {
    // Wait for MSAL to finish initialization (checking for redirect)
    await firstValueFrom(
      race(
        authService.msalInitialized$.pipe(
          filter(initialized => initialized),
          take(1)
        ),
        timer(10000).pipe(map(() => {
          console.warn('[AuthGuard] MSAL initialization timeout');
          return true;
        }))
      )
    );

    // Check authentication after MSAL has processed any redirect
    if (authService.isAuthenticated()) {
      return true;
    }
  } catch (error) {
    console.error('[AuthGuard] Error waiting for Azure AD processing:', error);
  }

  router.navigate([config.loginRoute || '/login']);
  return false;
};

export const adminGuard = async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const config = inject(IMC_AUTH_CONFIG);

  // Check if already authenticated as admin
  if (authService.isAuthenticated() && authService.isAdmin()) {
    return true;
  }

  // Wait for MSAL initialization (with 10 second timeout)
  try {
    await firstValueFrom(
      race(
        authService.msalInitialized$.pipe(
          filter(initialized => initialized),
          take(1)
        ),
        timer(10000).pipe(map(() => {
          console.warn('[AdminGuard] MSAL initialization timeout');
          return true;
        }))
      )
    );

    // Check authentication after MSAL has processed any redirect
    if (authService.isAuthenticated() && authService.isAdmin()) {
      return true;
    }
  } catch (error) {
    console.error('[AdminGuard] Error waiting for Azure AD processing:', error);
  }

  if (!authService.isAuthenticated()) {
    router.navigate([config.loginRoute || '/login']);
  } else {
    router.navigate([config.unauthorizedRoute || '/']);
  }

  return false;
};

/**
 * Factory function to create a group guard
 */
export const groupGuard = (groupName: string) => async () => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const config = inject(IMC_AUTH_CONFIG);

  // Check if already authenticated and has group access
  if (authService.isAuthenticated() && authService.hasGroup(groupName)) {
    return true;
  }

  // Wait for MSAL initialization (with 10 second timeout)
  try {
    await firstValueFrom(
      race(
        authService.msalInitialized$.pipe(
          filter(initialized => initialized),
          take(1)
        ),
        timer(10000).pipe(map(() => {
          console.warn(`[GroupGuard:${groupName}] MSAL initialization timeout`);
          return true;
        }))
      )
    );

    // Check authentication after MSAL has processed any redirect
    if (authService.isAuthenticated() && authService.hasGroup(groupName)) {
      return true;
    }
  } catch (error) {
    console.error(`[GroupGuard:${groupName}] Error waiting for Azure AD processing:`, error);
  }

  if (!authService.isAuthenticated()) {
    router.navigate([config.loginRoute || '/login']);
  } else {
    router.navigate([config.unauthorizedRoute || '/']);
  }

  return false;
};

// Pre-defined group guards
export const dashboardGuard = groupGuard('Dashboard');
export const configGuard = groupGuard('Config');
